import React from "react";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintingVM from "../viewmodel/PrintingVM";
import { AddPrintKeys } from "../../../label_print/assets/VarConstants";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import SuccessPopup from "./SuccessPopup";
import { useTranslation } from "react-i18next";

const WarehousePopUp = ({
  open,
  onClose,
  material,
  bin_location,
  storage_loc,
}) => {
  const {
    sizeOptions,
    selectedSize,
    stickerData,
    handleStickerDataEdit,
    buttonJson,
    handleSizeChange,
    handleButtonClick,
    resetToDefaults,
    loading,
    showSuccessPopup,
    successMessage,
    handleCloseSuccessPopup,
  } = PrintingVM({
    material,
    bin_location,
    storage_loc,
    popupType: "warehouse",
  });
  const { t } = useTranslation();

  const handleClose = () => {
    resetToDefaults();
    onClose();
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            margin: { xs: "0.5rem", sm: "2rem" },
            width: { xs: "98%", sm: "90%", md: "80%", lg: "70%" },
            maxWidth: "500px",
            borderRadius: { xs: "8px", sm: "12px" },
            maxHeight: { xs: "95vh", sm: "auto" },
          },
        }}
      >
        <CBackDrop cOpen={loading} />

        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: { xs: "1rem 1rem 0.5rem 1rem", sm: "2rem 2rem 1rem 2rem" },
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 600,
              padding: { xs: "0 0.5rem", sm: "0 1rem" },
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
            }}
          >
            {t('warehouse_label')}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "#666",
              padding: { xs: "0.5rem", sm: "0.75rem" },
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: { xs: "0.5rem", sm: "1rem" } }}>
          <Grid
            container
            spacing={{ xs: 2, sm: 3 }}
            sx={{ padding: { xs: "1rem", sm: "2rem" } }}
          >
            {/* First Row - Size */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    "&.MuiInputLabel-shrunk": {
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    },
                  }}
                >
                  {t('size')}
                </InputLabel>
                <Select
                  value={selectedSize?.id || ""}
                  onChange={(e) => {
                    const selectedOption = sizeOptions.find(
                      (item) => item.id === e.target.value
                    );
                    handleSizeChange(selectedOption);
                  }}
                  label={t('size')}
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  {sizeOptions
                    .filter((item) => item.is_warehouse !== 1)
                    .map((option) => (
                      <MenuItem
                        key={option.id}
                        value={option.id}
                        sx={{
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {option.size}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Second Row - Bin Location */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t('bin_loc')}
                value={stickerData[AddPrintKeys.BIN_LOCATION_KEY] || ""}
                onChange={(e) =>
                  handleStickerDataEdit(AddPrintKeys.BIN_LOCATION_KEY)(
                    e.target.value
                  )
                }
                required
                sx={{
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    "&.MuiInputLabel-shrunk": {
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                }}
              />
            </Grid>

            {/* Third Row - Storage Bin Location */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t('str_loc')}
                value={stickerData[AddPrintKeys.STORAGE_LOC_KEY] || ""}
                onChange={(e) =>
                  handleStickerDataEdit(AddPrintKeys.STORAGE_LOC_KEY)(
                    e.target.value
                  )
                }
                required
                sx={{
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    "&.MuiInputLabel-shrunk": {
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                }}
              />
            </Grid>

            {/* Fourth Row - Action Buttons */}
            <Grid item xs={12} sx={{ mt: { xs: 1, sm: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: { xs: 3, sm: 3 },
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {Object.keys(buttonJson).map((key) => (
                  <Tooltip key={key} title={buttonJson[key].tooltipText}>
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleButtonClick(key, handleClose)}
                        disabled={buttonJson[key].isDisabled}
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          padding: { xs: "0.5rem", sm: "0.5rem" },
                          minHeight: { xs: "40px", sm: "44px" },
                          minWidth: { xs: "40px", sm: "44px" },
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "6px",
                          "& .MuiSvgIcon-root": {
                            fontSize: { xs: "1.2rem", sm: "1.5rem" },
                          },
                        }}
                      >
                        {buttonJson[key].src}
                      </Button>
                    </span>
                  </Tooltip>
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      {/* Success Popup */}
      <SuccessPopup
        open={showSuccessPopup}
        onClose={handleCloseSuccessPopup}
        message={successMessage}
      />
    </>
  );
};

export default WarehousePopUp;
